import { type FC, useId, useState } from "react";
import { useFormState } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "./ErrorMessageBanner";
import { FileUploadController } from "./FileUploadController";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { LoadingDotsIcon } from "mds/icons";

type LabeledFileUploadControllerProps = React.InputHTMLAttributes<HTMLInputElement> & {
  wrapperClassName?: string;
  name: string;
  label: string;
  postLabel?: React.ReactNode;
  required?: boolean;
};

export const LabeledFileUploadController: FC<LabeledFileUploadControllerProps> = ({
  id: idProp,
  className,
  wrapperClassName,
  name,
  label,
  postLabel,
  required = false,
  ...inputProps
}) => {
  const randomId = useId();
  const formState = useFormState({ name });
  const id = idProp || `${randomId}-${name}`;
  const errorId = `${id}-error`;

  const [isUploading, setIsUploading] = useState(false);

  const labelNode = (
    <label className="font-medium text-black-tint-20" htmlFor={id}>
      {label}
    </label>
  );

  return (
    <div className={twMerge("flex flex-col", wrapperClassName)}>
      <div className="flex items-center justify-between">
        {postLabel ? (
          <div className="flex items-center gap-2">
            {labelNode}
            {postLabel}
          </div>
        ) : (
          labelNode
        )}
        {!required && <div className="font-normal text-black-tint-40">{t("common.optional")}</div>}
      </div>
      <FileUploadController
        name={name}
        render={({ value, field, select }) => (
          <div className={twMerge("flex items-center gap-2", className)}>
            <input
              {...field}
              {...inputProps}
              aria-errormessage={formState.errors[name] ? errorId : undefined}
              aria-invalid={formState.errors[name] ? "true" : "false"}
              className="sr-only"
              id={id}
            />
            {value && (
              <div className="w-20 truncate rounded border border-black-tint-90 bg-white px-1.5 py-1 font-normal">
                {value}
              </div>
            )}
            <SquareButton kind="secondary" size="m" onClick={select}>
              {value ? t("common.replace") : t("common.upload")}
            </SquareButton>
            {isUploading ? <LoadingDotsIcon /> : null}
          </div>
        )}
        rules={{ required: required ? t("error.field_required") : false }}
        uploadFiles={async (files) => {
          setIsUploading(true);
          const resp = await collaborationUploadFile(files[0]);
          setIsUploading(false);
          return resp.url;
        }}
      />
      <ErrorMessageBanner id={errorId} name={name} />
    </div>
  );
};
